import { useState } from 'react';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart, apiErrorMessage } from '@/contexts/CartContext';
import ProductImage from '@/components/ProductImage';
import { Link, useNavigate } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';
import { CART } from '@/constants/testIds';
import { toast } from 'sonner';
import { DELIVERY_FEE } from '@/constants/shop';

const Cart = () => {
  const { cart, updateQuantity, removeItem, clearCart, isCartOpen: open, closeCart: onClose } = useCart();
  const [busyItemId, setBusyItemId] = useState(null);
  const navigate = useNavigate();
  const deliveryFee = DELIVERY_FEE;

  const handleQuantityChange = async (item, newQuantity) => {
    setBusyItemId(item.id);
    try {
      if (newQuantity <= 0) {
        await removeItem(item.id);
      } else {
        await updateQuantity(item.id, newQuantity);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'فشل تحديث السلة'));
    } finally {
      setBusyItemId(null);
    }
  };

  const handleClear = async () => {
    try {
      await clearCart();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'فشل إفراغ السلة'));
    }
  };

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const total = (cart.total_amount || 0) + deliveryFee;

  return (
    <>
      <div
        className={`cart-overlay ${open ? 'open' : ''}`}
        onClick={onClose}
      />
      <div
        data-testid={CART.drawer}
        className={`cart-drawer ${open ? 'open' : ''}`}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-tajawal font-bold">السلة</h2>
            <button onClick={onClose} className="hover:bg-gray-100 p-2 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>

          {!cart.items?.length ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500 font-cairo">السلة فارغة</p>
            </div>
          ) : (
            <>
              <div
                data-testid={CART.itemList}
                className="flex-1 overflow-y-auto p-6 space-y-4"
              >
                {cart.items?.map((item) => (
                  <div key={item.id} className={`flex gap-4 pb-4 border-b ${busyItemId === item.id ? 'opacity-60 pointer-events-none' : ''}`}>
                    <Link to={`/products/${item.product_id}?variant=${item.variant_id}`} onClick={onClose} className="shrink-0">
                      <ProductImage
                        src={item.image_url}
                        name={item.name_ar}
                        className="w-20 h-20 object-cover rounded-lg"
                        textClassName="text-[11px]"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/products/${item.product_id}?variant=${item.variant_id}`}
                        onClick={onClose}
                        className="font-cairo font-semibold line-clamp-1 hover:text-burgundy-500 transition-colors"
                      >
                        {item.name_ar}
                      </Link>
                      {item.options?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-gray-500 font-cairo">
                          {item.options.map((option) => (
                            <span key={option.name} className="inline-flex items-center gap-1">
                              {option.hex && (
                                <span className="w-3 h-3 rounded-full ring-1 ring-black/15" style={{ backgroundColor: option.hex }} />
                              )}
                              {option.value}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-burgundy-500 font-bold mt-1">
                        {formatPrice(item.price)}
                      </p>
                      {!item.available && (
                        <p className="text-xs text-destructive font-cairo mt-1">
                          هذا المنتج غير متاح حالياً
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleQuantityChange(item, item.quantity - 1)}
                          aria-label="تقليل الكمية"
                          className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          disabled={item.quantity >= 99}
                          aria-label="زيادة الكمية"
                          className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleQuantityChange(item, 0)}
                          aria-label="حذف من السلة"
                          className="mr-auto text-destructive hover:bg-red-50 p-1 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 border-t space-y-3">
                <div className="flex justify-between font-cairo">
                  <span>المجموع الفرعي</span>
                  <span data-testid={CART.subtotal}>{formatPrice(cart.total_amount || 0)}</span>
                </div>
                <div className="flex justify-between font-cairo">
                  <span>رسوم التوصيل</span>
                  <span data-testid={CART.deliveryFee}>{formatPrice(deliveryFee)}</span>
                </div>
                <div className="flex justify-between font-tajawal text-lg font-bold border-t pt-3">
                  <span>الإجمالي</span>
                  <span data-testid={CART.total} className="text-burgundy-500">
                    {formatPrice(total)}
                  </span>
                </div>
                <Button
                  data-testid={CART.checkoutBtn}
                  onClick={handleCheckout}
                  className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-lg py-6"
                >
                  إتمام الطلب
                </Button>
                <Button
                  data-testid={CART.clearBtn}
                  onClick={handleClear}
                  variant="outline"
                  className="w-full"
                >
                  إفراغ السلة
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
